import {
  RenterAssessmentParams,
  RenterAssessmentItem,
  RenterAccessmentBadge,
} from '../../interfaces/renter-profile-assessment.interface';

export const moveInDateAssessmentBadges: {
  asap: RenterAccessmentBadge;
  within30: RenterAccessmentBadge;
  misaligned: RenterAccessmentBadge;
} = {
  asap: { title: 'Can move in ASAP', badgeColor: 'green' },
  within30: { title: 'Compatible move-in date', badgeColor: 'green' },
  misaligned: { title: 'Move-in date too far out', badgeColor: 'red' },
};

function isValidYMD(date: string | null | undefined): boolean {
  return !!date && /^\d{4}-\d{2}-\d{2}$/.test(date);
}

function isValidISODate(date: string | null | undefined): boolean {
  return !!date && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/.test(date);
}

function extractDatePart(dateString: string): string {
  // Extract YYYY-MM-DD from ISO date string like "2025-05-01T00:00:00.000Z"
  return dateString.slice(0, 10);
}

function formatToMMDDYYYY(date: string): string {
  const [y, m, d] = date.split('-');
  return `${m}/${d}/${y}`;
}

export function getMoveInDateAssessment(assessmentParams: RenterAssessmentParams): RenterAssessmentItem {
  const renterDateRaw = assessmentParams.screening?.desiredMoveInDate || assessmentParams.renter?.desiredMoveInDate;
  const propertyDateRaw = assessmentParams.property?.leaseConditions?.desiredLeasingDate;

  if (!renterDateRaw || !propertyDateRaw) {
    return { uiRepresentation: renterDateRaw || 'N/A', badge: null };
  }

  // Extract date part from property date if it's in ISO format
  const propertyDateForValidation = isValidISODate(propertyDateRaw)
    ? extractDatePart(propertyDateRaw)
    : propertyDateRaw;

  // If not valid YYYY-MM-DD format after extraction, return as-is
  if (!isValidYMD(renterDateRaw) || !isValidYMD(propertyDateForValidation)) {
    return { uiRepresentation: renterDateRaw, badge: null };
  }

  // Parse dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const propertyDate = new Date(propertyDateForValidation);
  propertyDate.setHours(0, 0, 0, 0);
  const renterDate = new Date(renterDateRaw);
  renterDate.setHours(0, 0, 0, 0);

  // Constant based on backend logic
  const acceptablePeriodDays = 40;

  // Calculate days from current date to renter's desired move-in date
  const daysFromToday = Math.round((renterDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  // Calculate days from current date to property availability
  const daysToPropertyAvailable = Math.round((propertyDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  const uiRepresentation = formatToMMDDYYYY(renterDateRaw);
  let badge: RenterAccessmentBadge | null = null;

  // Determine badge based on backend logic and your requirements
  if (daysToPropertyAvailable <= 0) {
    // Property is already available
    if (daysFromToday <= 14) {
      // Renter can move in within 14 days - ASAP badge
      badge = moveInDateAssessmentBadges.asap;
    } else if (daysFromToday <= 30) {
      // Renter can move in within 30 days - Compatible badge
      badge = moveInDateAssessmentBadges.within30;
    } else if (daysFromToday > acceptablePeriodDays) {
      // More than 40 days - Misaligned
      badge = moveInDateAssessmentBadges.misaligned;
    } else {
      // Between 30-40 days - no badge
      badge = null;
    }
  } else if (daysToPropertyAvailable <= acceptablePeriodDays) {
    // Property is available within 40 days from today
    if (daysFromToday < daysToPropertyAvailable) {
      // Renter wants to move in before property is available - no badge (incompatible timing)
      badge = null;
    } else if (daysFromToday <= 30) {
      // Renter wants to move in within 30 days from today - Compatible
      badge = moveInDateAssessmentBadges.within30;
    } else if (daysFromToday > acceptablePeriodDays) {
      // More than 40 days from today - Misaligned
      badge = moveInDateAssessmentBadges.misaligned;
    } else {
      // Between 30-40 days - no badge
      badge = null;
    }
  } else {
    // Property is available in more than 40 days - look for renters who can move in when property becomes available
    const daysFromPropertyToRenter = Math.round(
      (renterDate.getTime() - propertyDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysFromPropertyToRenter < 0) {
      // Renter wants to move in before property is available - no badge (incompatible timing)
      badge = null;
    } else if (daysFromPropertyToRenter === 0) {
      // Renter wants to move in exactly when property becomes available - Compatible
      badge = moveInDateAssessmentBadges.within30;
    } else if (daysFromPropertyToRenter <= 7) {
      // Renter wants to move in within 1 week after property becomes available - Compatible
      badge = moveInDateAssessmentBadges.within30;
    } else {
      // Renter wants to move in more than 1 week after property becomes available - Misaligned
      badge = moveInDateAssessmentBadges.misaligned;
    }
  }

  return { uiRepresentation, badge };
}
